import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Snackbar, SnackbarContent, Button } from '@mui/material';
import { createFairtreeTheme } from './resources/theme';

import { useDispatch, useSelector } from 'react-redux';
import MuiAlert from '@mui/material/Alert';

import PublicRoute from './routes/PublicRoute';
import PrivateRoute from './routes/PrivateRoute';
import { Roles } from './helpers/roles';

import Welcome from './pages/PublicPages/Welcome';
import SignUpLanding from './pages/PublicPages/SignupLanding';
import AuthCallback from './pages/PublicPages/AuthCallback';

import Home from './pages/PrivatePages/Home';
import MyProfile from './pages/PrivatePages/MyProfile';
import MyClients from './pages/PrivatePages/MyClients';
import MyPractice from './pages/PrivatePages/MyPractice';
import SystemAdmin from './pages/PrivatePages/SystemAdmin';
import ClientServices from './pages/PrivatePages/ClientServices';
import MDDs from './pages/PrivatePages/SystemAdmin/mdd';
import Reports from './pages/PrivatePages/SystemAdmin/repports';
import GuidanceTool from './pages/PrivatePages/ClientService/guidance';
import BrokerCodes from './pages/PrivatePages/Advisor/BrokerCodes';
import AdvisorClients from './pages/PrivatePages/Advisor/AdvisorClients';
import AdvisorClientAccounts from './pages/PrivatePages/Advisor/AdvisorClientAccounts';
import AdvisorClientAccount from './pages/PrivatePages/Advisor/AdvisorClientAccount';
import PracticeClients from './pages/PrivatePages/myPractise/clients/PracticeClients';
import AdminClients from './pages/PrivatePages/ClientService/clients/AdminClients';
import AdminClientAccounts from './pages/PrivatePages/ClientService/clients/AdminClientAccounts';
import AdminClientAccount from './pages/PrivatePages/ClientService/clients/AdminClientAccount';
import SubmitTicket from './pages/PrivatePages/SubmitTicket';
import MyAccounts from './pages/PrivatePages/myAccounts/myAccounts';
import MyAccount from './pages/PrivatePages/myAccounts/myAccount';
import Providers from './pages/PrivatePages/Providers/providers';
import Contact from './pages/PrivatePages/Contact';
import ComingSoon from './pages/PrivatePages/ComingSoon';

import PublicLayout from './layout/PublicLayout';
import WelcomeLayout from './layout/WelcomeLayout';
import PrivateLayout from './layout/PrivateLayout';

import { getSession } from './redux/auth/auth.slice';
import { clearAlert } from './redux/alert/alert.slice';
import TitleListener from './utils/titleListener';
import Logout from './pages/PublicPages/Logout';
import HouseManagement from './pages/PrivatePages/myPractise/houseManagement';
import ProviderAdvisors from './pages/PrivatePages/myPractise/providerAdvisors';
import Advisors from './pages/PrivatePages/myPractise/Advisors';
import AdvisorCodes from './pages/PrivatePages/myPractise/AdvisorCodes';
import PracticeClientAccounts from './pages/PrivatePages/myPractise/clients/PracticeClientAccounts';
import PracticeClientAccount from './pages/PrivatePages/myPractise/clients/PracticeClientAccount';
import ViewProviderBook from './pages/PrivatePages/myPractise/bookAnalysis/viewProviderBook';
import ViewAdvisorBook from './pages/PrivatePages/myPractise/bookAnalysis/viewAdvisorBook';
import BookAnalysis from './pages/PrivatePages/myPractise/bookAnalysis/bookAnalysis';
import { registerServiceWorker } from './services/serviceWorkerRegistration';
import NoAccess from './pages/PrivatePages/myAccounts/noAccess';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
function App() {
  const theme = createFairtreeTheme('light');

  const { message, type, open } = useSelector((state) => state.alert);
  const dispatch = useDispatch();
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState(null);
  const { loggingOut } = useSelector((state) => state.auth);

  const handleClose = () => dispatch(clearAlert());
  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('Service worker registered:', registration);

        registration.onupdatefound = () => {
          const newWorker = registration.installing;

          if (!newWorker) {
            return;
          }

          newWorker.onstatechange = () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New application version available');

              setServiceWorkerRegistration(registration);
              setUpdateAvailable(true);
            }
          };
        };
      })
      .catch((error) => {
        console.error('Service worker registration failed:', error);
      });
  }, []);

  const updateApplication = () => {
    if (serviceWorkerRegistration?.waiting) {
      serviceWorkerRegistration.waiting.postMessage({
        type: 'SKIP_WAITING'
      });
    }
  };
  useEffect(() => {
    const handleControllerChange = () => {
      window.location.reload();
    };

    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);
  useEffect(() => {
    const restore = async () => {
      try {
        await dispatch(getSession()).unwrap();
      } catch (error) {
      } finally {
        setLoadingAuth(false);
      }
    };

    restore();
  }, [dispatch]);

  if (loadingAuth || loggingOut) return null;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TitleListener />

      {/* Alerts */}
      <Snackbar
        sx={{ marginBottom: '58px' }}
        open={open}
        autoHideDuration={60000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleClose} severity={type} sx={{ whiteSpace: 'pre-line' }}>
          {typeof message === 'string' && message.trim()
            ? message
            : message?.message
            ? [message.message, message.details].filter(Boolean).join('\n')
            : type === 'error'
            ? 'An unknown error occurred. Please report this to the administrator.'
            : ''}
        </Alert>
      </Snackbar>
      <Snackbar
        open={updateAvailable}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}>
        <SnackbarContent
          message="A new version of the application is available."
          action={
            <Button color="inherit" size="small" onClick={updateApplication}>
              Update
            </Button>
          }
        />
      </Snackbar>
      <BrowserRouter>
        <Routes>
          {/* ================= PUBLIC ================= */}
          <Route element={<PublicRoute />}>
            <Route
              path="/"
              element={
                <WelcomeLayout>
                  <Welcome />
                </WelcomeLayout>
              }
            />
            <Route
              path="/signup/select"
              element={
                <PublicLayout>
                  <SignUpLanding />
                </PublicLayout>
              }
            />
            <Route
              path="/auth/callback"
              element={
                <PublicLayout>
                  <AuthCallback />
                </PublicLayout>
              }
            />
            <Route
              path="/logout"
              element={
                <PublicLayout>
                  <Logout />
                </PublicLayout>
              }
            />
          </Route>

          {/* ================= AUTHENTICATED (ALL ROLES) ================= */}
          <Route element={<PrivateRoute />}>
            <Route
              path="/my-investments"
              element={
                <PrivateLayout>
                  <Home />
                </PrivateLayout>
              }
            />
            <Route
              path="/balances"
              element={
                <PrivateLayout>
                  <MyAccounts />
                </PrivateLayout>
              }
            />
            <Route
              path="/portfolio"
              element={
                <PrivateLayout>
                  <MyAccount />
                </PrivateLayout>
              }
            />
            <Route
              path="/contact"
              element={
                <PrivateLayout>
                  <Contact />
                </PrivateLayout>
              }
            />
            <Route
              path="/user/profile"
              element={
                <PrivateLayout>
                  <MyProfile />
                </PrivateLayout>
              }
            />
            <Route
              path="/no-access"
              element={
                <PrivateLayout>
                  <NoAccess />
                </PrivateLayout>
              }
            />
          </Route>

          {/* ================= WEALTH MANAGER ================= */}
          <Route element={<PrivateRoute allowedRoles={Roles.WealthManager} />}>
            <Route
              path="/my-clients"
              element={
                <PrivateLayout>
                  <MyClients />
                </PrivateLayout>
              }
            />
            <Route
              path="/advisor/clients"
              element={
                <PrivateLayout>
                  <AdvisorClients />
                </PrivateLayout>
              }
            />
            <Route
              path="/advisor/clients/:id"
              element={
                <PrivateLayout>
                  <AdvisorClientAccounts />
                </PrivateLayout>
              }
            />
            <Route
              path="/advisor/client/account"
              element={
                <PrivateLayout>
                  <AdvisorClientAccount />
                </PrivateLayout>
              }
            />
            <Route
              path="/advisor/codes"
              element={
                <PrivateLayout>
                  <BrokerCodes />
                </PrivateLayout>
              }
            />
            <Route
              path="/advisor/submitTicket"
              element={
                <PrivateLayout>
                  <SubmitTicket />
                </PrivateLayout>
              }
            />
          </Route>

          {/* ================= CLIENT SERVICES ================= */}
          <Route element={<PrivateRoute allowedRoles={Roles.ClientServices} />}>
            <Route
              path="/client-services"
              element={
                <PrivateLayout>
                  <ClientServices />
                </PrivateLayout>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <PrivateLayout>
                  <AdminClients />
                </PrivateLayout>
              }
            />
            <Route
              path="/admin/clients/:id"
              element={
                <PrivateLayout>
                  <AdminClientAccounts />
                </PrivateLayout>
              }
            />
            <Route
              path="/admin/client/account/:id"
              element={
                <PrivateLayout>
                  <AdminClientAccount />
                </PrivateLayout>
              }
            />
            <Route
              path="/admin/fundfilter"
              element={
                <PrivateLayout>
                  <GuidanceTool />
                </PrivateLayout>
              }
            />
          </Route>

          {/* ================= MANAGEMENT ================= */}
          <Route element={<PrivateRoute allowedRoles={Roles.Management} />}>
            <Route
              path="/my-practice"
              element={
                <PrivateLayout>
                  <MyPractice />
                </PrivateLayout>
              }
            />
            <Route
              path="/practice/clients"
              element={
                <PrivateLayout>
                  <PracticeClients />
                </PrivateLayout>
              }
            />
            <Route
              path="/practice/clients/:id"
              element={
                <PrivateLayout>
                  <PracticeClientAccounts />
                </PrivateLayout>
              }
            />
            <Route
              path="/practice/client/account/:id"
              element={
                <PrivateLayout>
                  <PracticeClientAccount />
                </PrivateLayout>
              }
            />
            <Route
              path="/admin/client/account/:id"
              element={
                <PrivateLayout>
                  <AdminClientAccount />
                </PrivateLayout>
              }
            />
            <Route
              path="/my-practice/house-management"
              element={
                <PrivateLayout>
                  <HouseManagement />
                </PrivateLayout>
              }
            />

            <Route
              path="/my-practice/advisor-book-analysis/:id"
              element={
                <PrivateLayout>
                  <ViewAdvisorBook />
                </PrivateLayout>
              }
            />

            <Route
              path="/my-practice/provider-book-analysis"
              element={
                <PrivateLayout>
                  <BookAnalysis />
                </PrivateLayout>
              }
            />

            <Route
              path="/my-practice/provider-book-analysis/:id"
              element={
                <PrivateLayout>
                  <ViewProviderBook />
                </PrivateLayout>
              }
            />
            <Route
              path="/provider/advisors/:id"
              element={
                <PrivateLayout>
                  <ProviderAdvisors />
                </PrivateLayout>
              }
            />
            <Route
              path="/my-practice/advisors"
              element={
                <PrivateLayout>
                  <Advisors />
                </PrivateLayout>
              }
            />

            <Route
              path="/my-practice/advisors/:id"
              element={
                <PrivateLayout>
                  <AdvisorCodes />
                </PrivateLayout>
              }
            />
          </Route>

          {/* ================= SUPPORT ================= */}
          <Route element={<PrivateRoute allowedRoles={Roles.Support} />}>
            <Route
              path="/system-admin"
              element={
                <PrivateLayout>
                  <SystemAdmin />
                </PrivateLayout>
              }
            />
            <Route
              path="/providers"
              element={
                <PrivateLayout>
                  <Providers />
                </PrivateLayout>
              }
            />
            <Route
              path="/mdds"
              element={
                <PrivateLayout>
                  <MDDs />
                </PrivateLayout>
              }
            />
            <Route
              path="/reports"
              element={
                <PrivateLayout>
                  <Reports />
                </PrivateLayout>
              }
            />
          </Route>

          {/* ================= FALLBACK ================= */}
          <Route element={<PrivateRoute />}>
            <Route
              path="*"
              element={
                <PrivateLayout>
                  <ComingSoon />
                </PrivateLayout>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export { App };
